import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useEffect } from 'react';
import { Checkbox, Labeled } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IFileUploaderValue } from '~/components/organisms/fields/FileUploader';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IAdFormat, IAdLocation } from '~/lib/api/modules/Ads';
import { FormField } from '~/components/atoms/FormField';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { AdFormMainSection } from './AdFormMainSection';

export type IAdFormValues = {
  name?: string;
  url?: string;
  medias?: Array<IFileUploaderValue<'picture' | 'video'>>;
  location?: IAdLocation;
  feedFormat?: IAdFormat;
  feedPosition?: string;
  feedIndex?: string;
  feedRecurrentStep?: string;
  feedReccurentMaxIndex?: string;
  articlePosition?: string;
  maxDisplays?: string;
  maxClicks?: string;
  startAt?: string;
  endAt?: string;
  disabled?: boolean;
};

export type IAdFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IAdFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    appId: string;
    mode: IFormMode;
    defaultValues?: DefaultValues<IAdFormValues>;
    onSubmit?: SubmitHandler<IAdFormValues>;
    onDisabledStatusChange?: (disabled: boolean) => void;
  };

export const AdForm: React.FC<IAdFormProps> = (props) => {
  const {
    sx,
    appId,
    mode,
    defaultValues,
    onSubmit,
    onDisabledStatusChange,
    ...other
  } = props;
  const { t } = useTranslation();

  const form = usePersistentForm<IAdFormValues>({
    defaultValues,
  });
  useFormAutoFocus({ form, name: 'name' });

  const disabled = form.watch('disabled');
  useEffect(
    () => onDisabledStatusChange?.(!!disabled),
    [onDisabledStatusChange, disabled],
  );

  return (
    <ContentBlock.FormBody
      sx={sx}
      form={form}
      onSubmit={onSubmit}
      extraActions={
        <Labeled
          label={t('AdForm.fields.disabled.label')}
          labelPosition="right"
        >
          <FormField name="disabled" control={form.control} as={Checkbox} />
        </Labeled>
      }
      {...other}
    >
      <AdFormMainSection appId={appId} form={form} mode={mode} />
    </ContentBlock.FormBody>
  );
};
